/**
 * The single path from the browser to the API.
 *
 * Every call goes through `request()`, which means the Firebase token attachment,
 * the error envelope handling and the response parsing exist exactly once. Nothing
 * in the app calls `fetch` directly.
 */

import type { z } from 'zod'

import { env } from '@/app/env'
import { currentIdToken } from '@/app/firebase'

/**
 * A typed error carrying the backend's own message.
 *
 * The backend deliberately writes customer-facing messages for 4xx and a fixed
 * generic string for 5xx, so showing `message` directly is safe and far more useful
 * than "Something went wrong". `requestId` is what support asks for.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  /** A conflict is nearly always something the person can resolve themselves —
   *  buy a seat, remove a member — so the UI treats it as guidance, not failure. */
  get isConflict(): boolean {
    return this.status === 409
  }
}

interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  /**
   * Zod schema for the response. Omit for endpoints that return no content.
   *
   * The input side is `unknown` rather than `T`, which is what the bare
   * `z.ZodType<T>` would have meant. That matters for any schema using `.default()`
   * or `.transform()`: those have an input type that differs from their output, so
   * `z.ZodType<T>` silently excludes exactly the schemas written to tolerate a field
   * an older backend does not send. And it is the honest signature — what arrives
   * here is parsed JSON, which is `unknown` by definition.
   */
  schema?: z.ZodType<T, z.ZodTypeDef, unknown>
  /** Skip the Authorization header for public endpoints. */
  anonymous?: boolean
  signal?: AbortSignal
  /** Extra request headers, such as a save's If-Match. */
  headers?: Record<string, string>
}

export async function request<T = void>(path: string, options: RequestOptions<T> = {}): Promise<T> {
  const { method = 'GET', body, schema, anonymous = false, signal } = options

  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }

  if (!anonymous) {
    const token = await currentIdToken()
    if (!token) {
      // Failing here rather than sending an unauthenticated request gives a
      // recognisable error the router can act on, instead of a 401 that looks like
      // a server problem.
      throw new ApiError('You are not signed in.', 401, 'unauthorized')
    }
    headers.Authorization = `Bearer ${token}`
  }

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(`${env.apiBaseUrl}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  })

  if (response.status === 204) {
    return undefined as T
  }

  const payload = await readJson(response)

  if (!response.ok) {
    throw toApiError(response.status, payload)
  }

  if (!schema) {
    return undefined as T
  }

  const parsed = schema.safeParse(payload)
  if (!parsed.success) {
    // A shape mismatch is our bug, not the customer's. It is surfaced as a 500-like
    // error so it is visible in development and reported rather than rendering a
    // page full of blanks.
    console.error(`Response from ${path} did not match its schema`, parsed.error.issues)
    throw new ApiError(
      'The server sent an unexpected response. Please reload, and let us know if it persists.',
      500,
      'schema_mismatch',
    )
  }
  return parsed.data
}

/** Downloads a CSV export, returning a blob URL the caller must revoke. */
export async function requestCsv(path: string): Promise<Blob> {
  const token = await currentIdToken()
  if (!token) throw new ApiError('You are not signed in.', 401, 'unauthorized')

  const response = await fetch(`${env.apiBaseUrl}${path}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'text/csv' },
  })
  if (!response.ok) {
    throw toApiError(response.status, await readJson(response))
  }
  return response.blob()
}

/**
 * Uploads bytes to a pre-signed URL.
 *
 * The one call in the app that does not go through `request()`, and deliberately so.
 * It must NOT carry our Authorization header — the URL is signed for a bucket that
 * has never heard of a Firebase token, and sending one would leak the token to
 * Google for no benefit. It must send exactly the headers the signature covered,
 * which is why they are passed in rather than assembled here: the content type is
 * part of what was signed, so a helpful default would break the upload.
 */
export async function uploadSigned(
  url: string,
  headers: Record<string, string>,
  body: Blob,
): Promise<void> {
  const response = await fetch(url, { method: 'PUT', headers, body })
  if (!response.ok) {
    // The bucket answers in XML, which is no use to anybody reading it in a portal.
    // The status is the actionable part: 403 is nearly always an expired signature,
    // which retrying from the top fixes.
    throw new ApiError(
      response.status === 403
        ? 'That upload link expired. Please try attaching the image again.'
        : 'The image could not be uploaded. Please try again.',
      response.status,
      'upload_failed',
    )
  }
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    // A non-JSON body from a JSON API means something between us and the service
    // answered — a proxy error page, most likely.
    return { error: { code: 'bad_gateway', message: text.slice(0, 200) } }
  }
}

function toApiError(status: number, payload: unknown): ApiError {
  const envelope = payload as
    | { error?: { code?: string; message?: string; request_id?: string } }
    | null

  const message =
    envelope?.error?.message ??
    (status >= 500
      ? 'Something went wrong on our side. Please try again.'
      : 'That request could not be completed.')

  return new ApiError(message, status, envelope?.error?.code ?? 'unknown', envelope?.error?.request_id)
}

/** Triggers a browser download for a blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Revoking immediately can cancel the download in some browsers; a tick is
  // enough for the navigation to have started.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
